import { useCallback, useEffect, useRef, useState } from 'react';
import { getApiUrl } from '../config/api';
import { fetchGexChainForSymbol } from '../services/gexChainDataService';
import type { MarketAnalyticsSnapshot, MarketRegime } from '../types/marketAnalytics';
import { buildMarketAnalyticsSnapshot, classifyRegime } from '../utils/marketRegimeEngine';
import {
  buildDteBuckets,
  buildVolFlowMetrics,
  enrichGexMetricsFromClientHistory,
  gexLevelsFromRows,
} from '../utils/gexAnalyticsMetrics';
import { buildVolAnalytics } from '../utils/volAnalyticsMetrics';
import { fetchIvIndicator } from '../services/ivTracking';
import { buildSpreadAnalytics } from '../utils/spreadAnalyticsMetrics';
import { buildGexStrikeBand } from '../utils/gexWallHeatmap';
import { buildPinAnalytics } from '../utils/pinAnalyticsMetrics';
import { buildTier2Metrics } from '../utils/tier2AnalyticsMetrics';
import { computeBreedenLitzenberger } from '../utils/breedenLitzenberger';
import { readStablePrimarySessionFirst } from '../utils/stableTabStorage';

import { recordRegimeTransition } from '../utils/regimeTransitionStore';

const POLL_MS = 90_000;
const REGIME_PREV_KEY = 'mmec_regime_prev_v1';

function loadPrevRegime(symbol: string): MarketRegime | null {
  try {
    const raw = localStorage.getItem(REGIME_PREV_KEY);
    if (!raw) return null;
    const map = JSON.parse(raw) as Record<string, MarketRegime>;
    return map[symbol] ?? null;
  } catch {
    return null;
  }
}

function savePrevRegime(symbol: string, regime: MarketRegime): void {
  try {
    const raw = localStorage.getItem(REGIME_PREV_KEY);
    const map = raw ? (JSON.parse(raw) as Record<string, MarketRegime>) : {};
    map[symbol] = regime;
    localStorage.setItem(REGIME_PREV_KEY, JSON.stringify(map));
  } catch {
    /* ignore */
  }
}

function normalizeApiSnapshot(snap: MarketAnalyticsSnapshot): void {
  if (!snap.dte_buckets?.length) snap.dte_buckets = [];
  if (!snap.gex_strike_band?.length) snap.gex_strike_band = [];
  if (!snap.vol_analytics || Object.keys(snap.vol_analytics).length === 0) {
    snap.vol_analytics = {
      atm_iv: null,
      otm_put_iv: null,
      otm_call_iv: null,
      surface_curvature: null,
      intraday_regime: 'midday',
      short_vrp: null,
      iv_har_forecast: null,
    };
  }
  if (!snap.pin_analytics || Object.keys(snap.pin_analytics).length === 0) {
    snap.pin_analytics = {
      gravity_strike: null,
      gravity_score: null,
      pin_defense_active: false,
      volume_clusters: [],
    };
  }
  if (!snap.tier2 || !snap.tier2.dealer_constraint) {
    snap.tier2 = {
      dealer_constraint: null,
      opening_drive: null,
      theta_cliff_minutes: null,
      gamma_theta_crossover_near: false,
      cwr_proxy: null,
    };
  }
  if (!snap.spread_analytics) {
    snap.spread_analytics = {
      optimal_wing_pct: null,
      optimal_dte_bucket: null,
      optimal_cwr: null,
      heatmap: [],
      comparisons: [],
    };
  }
  if (snap.vol_flow) {
    snap.vol_flow.vpin_rolling ??= null;
    snap.vol_flow.flow_toxicity ??= false;
  }
}

function mergeChainEnrichment(
  sym: string,
  snap: MarketAnalyticsSnapshot,
  rows: import('../services/gexCalculator').OptionRow[],
): MarketAnalyticsSnapshot {
  if (!rows.length) return snap;

  const levels = gexLevelsFromRows(rows);
  snap.gex = enrichGexMetricsFromClientHistory(sym, snap.gex);

  const volFlow = buildVolFlowMetrics(rows);
  snap.vol_flow = { ...snap.vol_flow, ...volFlow };

  const volAnalytics = buildVolAnalytics(rows, snap.gex.spot, snap.gex.minutes_to_close);
  snap.vol_analytics = { ...snap.vol_analytics, ...volAnalytics };
  if (volAnalytics.iv_har_forecast != null && volAnalytics.atm_iv != null) {
    snap.vol_flow.iv_har_gap = volAnalytics.atm_iv - volAnalytics.iv_har_forecast;
  }

  if (!snap.dte_buckets?.length) {
    snap.dte_buckets = buildDteBuckets(rows);
  }

  if (!snap.gex_strike_band?.length) {
    snap.gex_strike_band = buildGexStrikeBand(levels);
  }

  if (
    snap.pin_analytics.gravity_strike == null &&
    !snap.pin_analytics.volume_clusters?.length
  ) {
    snap.pin_analytics = buildPinAnalytics(
      levels,
      rows,
      snap.gex.spot,
      snap.gex.minutes_to_close,
    );
  }

  if (!snap.tier2.dealer_constraint) {
    snap.tier2 = buildTier2Metrics(
      snap.gex,
      snap.vol_flow,
      snap.vol_analytics,
      snap.dte_buckets,
      rows,
    );
  }

  if (!snap.spread_analytics?.heatmap?.length) {
    snap.spread_analytics = buildSpreadAnalytics(rows, snap.gex.spot, volAnalytics.atm_iv);
  }

  const { moments } = computeBreedenLitzenberger(rows, snap.gex.spot);
  const classified = classifyRegime(snap.gex, snap.vol_flow, moments, rows);
  snap.regime = classified.regime;
  snap.regime_confidence = classified.confidence;
  snap.risk_level = classified.risk_level;

  snap.factors = {
    ...snap.factors,
    dollar_gex: snap.gex.dollar_gex,
    gex_zscore: snap.gex.gex_zscore,
    trap_zone: snap.gex.trap_zone,
    pin_probability: snap.gex.pin_probability,
    z_vov: snap.vol_flow.z_vov,
    iv_har_gap: snap.vol_flow.iv_har_gap,
    signed_buyer_pcr: snap.vol_flow.signed_buyer_pcr,
    vpin_rolling: snap.vol_flow.vpin_rolling,
    flow_toxicity: snap.vol_flow.flow_toxicity,
    minutes_to_close: snap.gex.minutes_to_close,
    gamma_acceleration: snap.gex.gamma_acceleration,
    rnd_skewness: moments?.skewness ?? snap.factors?.rnd_skewness ?? null,
    dealer_constraint: snap.tier2.dealer_constraint,
    short_vrp: snap.vol_analytics.short_vrp,
    surface_curvature: snap.vol_analytics.surface_curvature,
    intraday_vol_regime: snap.vol_analytics.intraday_regime,
    pin_gravity_strike: snap.pin_analytics.gravity_strike,
    opening_drive: snap.tier2.opening_drive,
    cwr_proxy: snap.tier2.cwr_proxy,
    optimal_wing_pct: snap.spread_analytics.optimal_wing_pct,
    optimal_cwr: snap.spread_analytics.optimal_cwr,
  };

  return snap;
}

/**
 * `authToken` gates the fetches: the provider mounts inside AuthProvider, and a child's
 * effect runs before the parent's session-restore effect, so without this the first poll
 * went out with no credential and drew a 401. Signed out, nothing is fetched.
 */
export function useMarketAnalytics(symbol?: string, authToken?: string | null) {
  const sym = (symbol || readStablePrimarySessionFirst() || 'SPY').toUpperCase();
  const [snapshot, setSnapshot] = useState<MarketAnalyticsSnapshot | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const lastTransitionRef = useRef<string | null>(null);

  const refresh = useCallback(async () => {
    if (!authToken) return;
    setLoading(true);
    setError(null);
    try {
      let snap: MarketAnalyticsSnapshot | null = null;
      let fromApi = false;
      try {
        const res = await fetch(
          getApiUrl(`/api/analytics/snapshot?symbol=${encodeURIComponent(sym)}`),
        );
        if (res.ok) {
          const json = await res.json();
          if (json?.success && json.snapshot) {
            snap = json.snapshot as MarketAnalyticsSnapshot;
            fromApi = true;
            normalizeApiSnapshot(snap);
            if (snap.vol_flow) {
              snap.vol_flow.vpin_rolling ??= null;
              snap.vol_flow.flow_toxicity ??= false;
            }
          }
        }
      } catch {
        /* client fallback */
      }

      const { rows } = await fetchGexChainForSymbol(sym);

      if (!snap) {
        if (!rows.length) {
          setError('No chain data for analytics');
          setSnapshot(null);
          return;
        }
        const prev = loadPrevRegime(sym);
        snap = buildMarketAnalyticsSnapshot(sym, rows, prev);
      } else if (fromApi && rows.length) {
        snap = mergeChainEnrichment(sym, snap, rows);
      }

      // Overlay the REAL backend IV/vol forecast: replaces the client-side fake
      // realized-vol proxy (atmIV*0.85) and the fake DTE-bucket "HAR" with the
      // GARCH-HAR forecast + variance-risk-premium, and carries the spike signal
      // to the floating badge via factors. Best-effort so analytics still render.
      try {
        const ind = await fetchIvIndicator(sym);
        if (ind && ind.ok) {
          const iv = ind.implied_volatility ?? snap.vol_analytics.atm_iv;
          const f1 = ind.forecast?.ok ? ind.forecast.forecast_1d ?? null : null;
          const gap30 = ind.iv_rv_gap?.hv30 ?? null;
          if (f1 != null) {
            snap.vol_analytics.iv_har_forecast = f1;
            if (iv != null) snap.vol_flow.iv_har_gap = Number((iv - f1).toFixed(4));
          }
          if (gap30 != null) snap.vol_analytics.short_vrp = gap30;
          snap.factors = {
            ...snap.factors,
            iv_implied: iv,
            iv_rv_gap_hv30: gap30,
            garch_har_forecast_1d: f1,
            iv_spike_probability: ind.forecast?.ok ? ind.forecast.spike_probability ?? null : null,
            iv_spike_live: ind.forecast?.ok ? !!ind.forecast.live_breach : false,
            net_gex_bn: ind.gex?.ok ? ind.gex.net_gex_bn ?? null : null,
          };
        }
      } catch {
        /* keep chain-derived values when the indicator is unavailable */
      }

      if (snap.transitioned && snap.regime_prev) {
        recordRegimeTransition(sym, snap.regime_prev, snap.regime);
        const key = `${snap.regime_prev}->${snap.regime}@${snap.as_of.slice(0, 16)}`;
        if (lastTransitionRef.current !== key) {
          lastTransitionRef.current = key;
          window.dispatchEvent(
            new CustomEvent('mmec-regime-changed', { detail: snap }),
          );
        }
      }
      savePrevRegime(sym, snap.regime);
      setSnapshot(snap);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Analytics unavailable');
      setSnapshot(null);
    } finally {
      setLoading(false);
    }
  }, [sym, authToken]);

  useEffect(() => {
    refresh();
    const id = window.setInterval(refresh, POLL_MS);
    return () => window.clearInterval(id);
  }, [refresh]);

  return { snapshot, loading, error, refresh, symbol: sym };
}
